import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import "../../styles/popupStyle.css";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { textStyle } from "../../styles/Style";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveMiscItemsTooltipAllowBookEditPast,
  reserveMiscItemsTooltipAllowEditCheckedIn,
  reserveMiscItemsTooltipCancelEmail,
  reserveMiscItemsTooltipConfirmationEmail,
  reserveMiscItemsTooltipDefaultCommunicationPreference,
  reserveMiscItemsTooltipGuestWaiverFormRequired,
  reserveMiscItemsTooltipHeader,
  reserveMiscItemsTooltipPastDaysToViewEdit,
  reserveMiscItemsTooltipUpdateEmail,
} from "../../../constants/ReserveTooltipInformation";
import {
  normalizeReserveMiscSettings,
  RESERVE_MISC_DEFAULT_COMMUNICATION_PREFERENCE_OPTIONS,
  RESERVE_MISC_DEFAULT_SETTINGS,
  RESERVE_MISC_EMAIL_NOTIFY_OPTIONS,
  reserveMiscLegacyFlatObjectToRow,
  reserveMiscRowFromUnknown,
} from "../../../constants/ReserveMiscItemsConstants";
import { YesOrNo } from "../../../constants/YesOrNo";

const ReserveMiscItems = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [primaryId, setPrimaryId] = useState<number>(1);
  const [primaryCode, setPrimaryCode] = useState<string | null>(null);
  const [primaryName, setPrimaryName] = useState<string | null>(null);
  const [guestWaiverFormRequired, setGuestWaiverFormRequired] = useState<
    string | null
  >(RESERVE_MISC_DEFAULT_SETTINGS["Guest waiver form required"]);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS["Confirmation Email"],
  );
  const [updateEmail, setUpdateEmail] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS["Update Email"],
  );
  const [cancelEmail, setCancelEmail] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS["Cancel Email"],
  );
  const [allowBookEditPast, setAllowBookEditPast] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS[
      "Allow to book/edit reservations for past date/time"
    ],
  );
  const [allowEditCheckedIn, setAllowEditCheckedIn] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS["Allow to edit Checked-In Reservation"],
  );
  const [pastDaysToViewEdit, setPastDaysToViewEdit] = useState<string | null>(
    RESERVE_MISC_DEFAULT_SETTINGS[
      "No. of past days to view/edit past reservation"
    ],
  );
  const [defaultCommunicationPreference, setDefaultCommunicationPreference] =
    useState<string | null>(
      RESERVE_MISC_DEFAULT_SETTINGS["Default Communication Preference"],
    );

  useEffect(() => {
    if (fieldValues != null) {
      let row: Record<string, unknown>;
      if (Array.isArray(fieldValues) && fieldValues[0] != null) {
        row = reserveMiscRowFromUnknown(fieldValues[0]) as Record<
          string,
          unknown
        >;
      } else if (
        typeof fieldValues === "object" &&
        !Array.isArray(fieldValues)
      ) {
        row = reserveMiscRowFromUnknown(
          reserveMiscLegacyFlatObjectToRow(
            fieldValues as Record<string, unknown>,
          ),
        ) as Record<string, unknown>;
      } else {
        return;
      }
      const idRaw = row.id;
      setPrimaryId(typeof idRaw === "number" ? idRaw : Number(idRaw) || 1);
      setPrimaryCode(row.Code != null ? String(row.Code) : null);
      setPrimaryName(row.Name != null ? String(row.Name) : null);
      const s = normalizeReserveMiscSettings(row);
      setGuestWaiverFormRequired(s["Guest waiver form required"]);
      setConfirmationEmail(s["Confirmation Email"]);
      setUpdateEmail(s["Update Email"]);
      setCancelEmail(s["Cancel Email"]);
      setAllowBookEditPast(
        s["Allow to book/edit reservations for past date/time"],
      );
      setAllowEditCheckedIn(s["Allow to edit Checked-In Reservation"]);
      setPastDaysToViewEdit(
        s["No. of past days to view/edit past reservation"],
      );
      setDefaultCommunicationPreference(s["Default Communication Preference"]);
    }
  }, []);

  const payload = {
    "Guest waiver form required": guestWaiverFormRequired,
    "Confirmation Email": confirmationEmail,
    "Update Email": updateEmail,
    "Cancel Email": cancelEmail,
    "Allow to book/edit reservations for past date/time": allowBookEditPast,
    "Allow to edit Checked-In Reservation": allowEditCheckedIn,
    "No. of past days to view/edit past reservation": pastDaysToViewEdit,
    "Default Communication Preference": defaultCommunicationPreference,
  };

  const validateInput = () => {
    data([
      {
        id: primaryId,
        Code: primaryCode ?? "",
        Name: primaryName ?? "",
        ...payload,
      },
    ]);
  };

  useEffect(() => {
    validateInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- match GolfPropertyInformation: validateInput uses current fieldValues once; avoid re-running on parent array identity
  }, [
    primaryId,
    primaryCode,
    primaryName,
    guestWaiverFormRequired,
    confirmationEmail,
    updateEmail,
    cancelEmail,
    allowBookEditPast,
    allowEditCheckedIn,
    pastDaysToViewEdit,
    defaultCommunicationPreference,
  ]);

  return (
    <Stack>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Typography variant="h6" component="div" sx={textStyle}>
          {ReservePropertyEnum.miscItems}
          <Tooltip title={tooltipComponent(reserveMiscItemsTooltipHeader)}>
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Typography>

        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                Guest waiver form required
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipGuestWaiverFormRequired ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-guest-waiver-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-guest-waiver-label"
                label="Value"
                value={guestWaiverFormRequired ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setGuestWaiverFormRequired(e.target.value)
                }
              >
                {YesOrNo.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                Confirmation Email
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipConfirmationEmail ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-confirmation-email-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-confirmation-email-label"
                label="Value"
                value={confirmationEmail ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setConfirmationEmail(e.target.value)
                }
              >
                {RESERVE_MISC_EMAIL_NOTIFY_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>Update Email</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipUpdateEmail ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-update-email-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-update-email-label"
                label="Value"
                value={updateEmail ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setUpdateEmail(e.target.value)
                }
              >
                {RESERVE_MISC_EMAIL_NOTIFY_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>Cancel Email</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipCancelEmail ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-cancel-email-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-cancel-email-label"
                label="Value"
                value={cancelEmail ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setCancelEmail(e.target.value)
                }
              >
                {RESERVE_MISC_EMAIL_NOTIFY_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                Allow to book/edit reservations for past date/time
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipAllowBookEditPast ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-book-edit-past-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-book-edit-past-label"
                label="Value"
                value={allowBookEditPast ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setAllowBookEditPast(e.target.value)
                }
              >
                {YesOrNo.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                Allow to edit Checked-In Reservation
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipAllowEditCheckedIn ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-edit-checked-in-label">
                Value
              </InputLabel>
              <Select
                labelId="reserve-misc-edit-checked-in-label"
                label="Value"
                value={allowEditCheckedIn ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setAllowEditCheckedIn(e.target.value)
                }
              >
                {YesOrNo.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                No. of past days to view/edit past reservation
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipPastDaysToViewEdit ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              fullWidth
              size="small"
              label="Value"
              value={pastDaysToViewEdit ?? ""}
              onChange={(e) => setPastDaysToViewEdit(e.target.value)}
              sx={{ flex: 1 }}
              type="number"
            />
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ sm: "flex-start" }}
            gap={2}
          >
            <Stack
              className="form-two-col-row"
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: { sm: 340 }, flex: { sm: "0 0 42%" }, pt: 1 }}
            >
              <FormLabel sx={{ m: 0, fontWeight: 600 }}>
                Default Communication Preference
              </FormLabel>
              <Tooltip
                title={tooltipComponent(
                  reserveMiscItemsTooltipDefaultCommunicationPreference ??
                    reserveMiscItemsTooltipHeader,
                )}
              >
                <InfoIcon fontSize="small" sx={{ cursor: "help" }} />
              </Tooltip>
            </Stack>
            <FormControl fullWidth size="small" sx={{ flex: 1 }}>
              <InputLabel id="reserve-misc-comm-pref-label">Value</InputLabel>
              <Select
                labelId="reserve-misc-comm-pref-label"
                label="Value"
                value={defaultCommunicationPreference ?? ""}
                onChange={(e: SelectChangeEvent<string>) =>
                  setDefaultCommunicationPreference(e.target.value)
                }
              >
                {RESERVE_MISC_DEFAULT_COMMUNICATION_PREFERENCE_OPTIONS.map(
                  (opt) => (
                    <MenuItem key={opt} value={opt}>
                      {opt}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ReserveMiscItems;
